import React from 'react';
import { Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faLink, faUnlink, faCog } from '@fortawesome/free-solid-svg-icons';
import { DevicePairCardProps } from './types';
import {
  PairCard,
  DeviceSection,
  DeviceName,
  DeviceIdentifier,
  ConnectionLine,
  ConnectionIcon,
  ActionsContainer,
  ActionGroup,
  PairCardContent,
  DeviceSectionContent,
  DeviceInfo,
  StyledBadge,
  ConnectionStatusContainer,
  StyledConnectionBadge,
  ActionIconButton,
  ConnectionIconWrapper,
} from './styles';
import { LABELS } from './constants';

export const DevicePairCard: React.FC<DevicePairCardProps> = ({
  pair,
  isDark,
  onEditKiosk,
  onEditReader,
  onDeleteKiosk,
  onDeleteReader,
  onManageConnection,
}) => {
  const isConnected = !!(pair.kiosk && pair.reader);

  return (
    <PairCard $isConnected={isConnected} $isDark={isDark}>
      <PairCardContent>
        <DeviceSection $type="kiosk" $isDark={isDark}>
          <DeviceSectionContent>
            <DeviceInfo>
              <DeviceName $isDark={isDark} $color={isDark ? '#90cdf4' : '#2b6cb0'}>
                {pair.kiosk ? pair.kiosk.name : LABELS.NO_KIOSK}
              </DeviceName>
              <DeviceIdentifier $isDark={isDark} $color={isDark ? '#63b3ed' : '#3182ce'}>
                {pair.kiosk ? pair.kiosk.identifier : LABELS.UNASSIGNED}
              </DeviceIdentifier>
            </DeviceInfo>
            <StyledBadge $isDark={isDark} $color="#3182ce" count={LABELS.KIOSK} />
          </DeviceSectionContent>
        </DeviceSection>

        {/* Connection Line */}
        <ConnectionLine $isConnected={isConnected} $isDark={isDark}>
          <ConnectionIcon $isConnected={isConnected} $isDark={isDark}>
            <ConnectionIconWrapper $isConnected={isConnected} $isDark={isDark}>
              <FontAwesomeIcon icon={isConnected ? faLink : faUnlink} />
            </ConnectionIconWrapper>
          </ConnectionIcon>
        </ConnectionLine>

        {/* Reader Section */}
        <DeviceSection $type="reader" $isDark={isDark}>
          <DeviceSectionContent>
            <DeviceInfo>
              <DeviceName $isDark={isDark} $color={pair.reader ? (isDark ? '#9ae6b4' : '#38a169') : (isDark ? '#718096' : '#a0aec0')}>
                {pair.reader ? pair.reader.name : LABELS.NO_READER}
              </DeviceName>
              <DeviceIdentifier $isDark={isDark} $color={pair.reader ? (isDark ? '#68d391' : '#48bb78') : (isDark ? '#a0aec0' : '#cbd5e0')}>
                {pair.reader ? pair.reader.identifier : LABELS.UNASSIGNED}
              </DeviceIdentifier>
            </DeviceInfo>
            <StyledBadge 
              $isDark={isDark} 
              $color={pair.reader ? '#38a169' : (isDark ? '#718096' : '#a0aec0')} 
              count={LABELS.READER} 
            />
          </DeviceSectionContent>
        </DeviceSection>

        {/* Connection Status */}
        <ConnectionStatusContainer>
          <StyledConnectionBadge
            $isDark={isDark}
            status={isConnected ? 'success' : 'warning'}
            text={isConnected ? LABELS.CONNECTED : LABELS.INCOMPLETE_PAIR}
          />
        </ConnectionStatusContainer>

        {/* Action Buttons */}
        <ActionsContainer>
          <ActionGroup>
            {pair.kiosk && (
              <Tooltip title={LABELS.EDIT_KIOSK}>
                <ActionIconButton
                  $isDark={isDark}
                  iconColor="#2D6CCA"
                  onClick={() => onEditKiosk(pair.kiosk)}
                >
                  <FontAwesomeIcon icon={faPen} fontSize={14} color={isDark ? '#fff' : '#222222'} />
                </ActionIconButton>
              </Tooltip>
            )}
            {pair.reader && (
              <Tooltip title={LABELS.EDIT_READER}>
                <ActionIconButton
                  $isDark={isDark}
                  iconColor="#2D6CCA"
                  onClick={() => onEditReader(pair.reader)}
                >
                  <FontAwesomeIcon icon={faPen} fontSize={14} color={isDark ? '#fff' : '#222222'} />
                </ActionIconButton>
              </Tooltip>
            )}
          </ActionGroup>
          <ActionGroup>
            {onManageConnection && (
              <Tooltip title={LABELS.MANAGE_CONNECTION}>
                <ActionIconButton
                  $isDark={isDark}
                  iconColor="#2D6CCA"
                  onClick={onManageConnection}
                >
                  <FontAwesomeIcon icon={faCog} fontSize={14} color={isDark ? '#fff' : '#222222'} />
                </ActionIconButton>
              </Tooltip>
            )}
            {pair.kiosk && (
              <Tooltip title={LABELS.DELETE_KIOSK}>
                <ActionIconButton
                  $isDark={isDark}
                  iconColor="#f5222d"
                  onClick={() => onDeleteKiosk(pair.kiosk!.id)}
                >
                  <FontAwesomeIcon icon={faTrash} fontSize={14} color={isDark ? '#fff' : '#222222'} />
                </ActionIconButton>
              </Tooltip>
            )}
            {pair.reader && (
              <Tooltip title={LABELS.DELETE_READER}>
                <ActionIconButton
                  $isDark={isDark}
                  iconColor="#f5222d"
                  onClick={() => onDeleteReader(pair.reader!.id)}
                >
                  <FontAwesomeIcon icon={faTrash} fontSize={14} color={isDark ? '#fff' : '#222222'} />
                </ActionIconButton>
              </Tooltip>
            )}
          </ActionGroup>
        </ActionsContainer>
      </PairCardContent>
    </PairCard>
  );
};
